import type { FC, ReactNode } from 'react'
import type { GovernorateOption, HomepageData, OfferCardData, ProductCardData, StoreSettings } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, BadgePercent, Layers, Truck, Wallet } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import { unitLabel } from '@/constants/units'
import { ButtonLink } from '@/components/ui/button'
import { CmsText } from '@/components/store/cms-text'
import { Countdown } from '@/components/store/countdown'
import { Num, Price } from '@/components/store/price'
import { ProductMedia } from '@/components/store/product-media'

interface HeroProps {
    homepage: HomepageData
    settings: StoreSettings
    spotlight: ProductCardData | null
    shippingSample: GovernorateOption | null
    liveOffer: OfferCardData | null
    governoratesCount: number
}

const BrandPattern: FC = (): ReactNode => (
    <svg viewBox='0 0 400 400' className='absolute inset-0 h-full w-full' aria-hidden='true'>
        <defs>
            <linearGradient id='hero-stroke' x1='0' y1='0' x2='1' y2='1'>
                <stop offset='0%' stopColor='#22d3ee' stopOpacity='0.9' />
                <stop offset='100%' stopColor='#6a84b5' stopOpacity='0.25' />
            </linearGradient>
        </defs>
        {[150, 118, 86, 54].map((radius, index) => {
            const points = Array.from({ length: 6 }, (_, corner) => {
                const angle = (Math.PI / 3) * corner - Math.PI / 2

                return `${200 + radius * Math.cos(angle)},${200 + radius * Math.sin(angle)}`
            }).join(' ')

            return <polygon key={radius} points={points} fill='none' stroke='url(#hero-stroke)' strokeWidth={index === 0 ? 2.5 : 1.5} strokeOpacity={1 - index * 0.18} strokeLinejoin='round' />
        })}
        <circle cx='200' cy='200' r='22' fill='#ffffff' fillOpacity='0.92' />
    </svg>
)

export const Hero: FC<HeroProps> = ({ homepage, settings, spotlight, shippingSample, liveOffer, governoratesCount }): ReactNode => {
    const { tx, txn, locale, isRtl } = useI18n()
    const Arrow = isRtl ? ArrowLeft : ArrowRight
    const spotlightPricing = spotlight?.pricing
    const showSpotlight = Boolean(spotlight && spotlightPricing && spotlightPricing.wholesaleFinal !== null)

    return (
        <section id='hero' className='relative overflow-hidden bg-gradient-to-b from-navy-50/80 via-white to-white' aria-labelledby='hero-title'>
            <div className='absolute inset-x-0 top-0 h-[520px] bg-dots [mask-image:linear-gradient(to_bottom,black,transparent)]' aria-hidden='true' />

            <div className='container-page relative grid items-center gap-12 pt-12 pb-16 sm:pt-16 lg:grid-cols-12 lg:gap-8 lg:pt-20 lg:pb-24'>
                <div className='flex flex-col gap-6 lg:col-span-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
                    {homepage.heroEyebrow && (
                        <span className='inline-flex w-fit items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-navy-800 shadow-soft ring-1 ring-navy-100 sm:text-sm'>
                            <span className='size-2 rounded-full bg-cyan-500' aria-hidden='true' />
                            <CmsText table='homepage_content' id={homepage.id} field={homepage.heroEyebrow} />
                        </span>
                    )}

                    <CmsText
                        table='homepage_content'
                        id={homepage.id}
                        field={homepage.heroTitle}
                        as='h1'
                        className='text-balance text-4xl font-bold leading-[1.2] tracking-tight text-navy-950 sm:text-5xl lg:text-[3.6rem] lg:leading-[1.15]'
                    />

                    {homepage.heroSubtitle && (
                        <CmsText table='homepage_content' id={homepage.id} field={homepage.heroSubtitle} as='p' className='max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground' />
                    )}

                    <div className='flex flex-wrap gap-3 pt-1'>
                        <ButtonLink href='/products' size='lg' className='group'>
                            <CmsText table='homepage_content' id={homepage.id} field={homepage.heroPrimaryCta} />
                            <Arrow className='size-4 transition-transform group-hover:-translate-x-0.5 ltr:group-hover:translate-x-0.5' aria-hidden='true' />
                        </ButtonLink>

                        {homepage.heroSecondaryCta && (
                            <ButtonLink href='/offers' variant='outline' size='lg'>
                                <CmsText table='homepage_content' id={homepage.id} field={homepage.heroSecondaryCta} />
                            </ButtonLink>
                        )}
                    </div>

                    <ul className='grid gap-3 pt-4 text-sm text-navy-700 sm:grid-cols-3'>
                        <li className='flex items-center gap-2.5'>
                            <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-white text-cyan-700 shadow-soft ring-1 ring-navy-100'><Truck className='size-4.5' aria-hidden='true' /></span>
                            <span>{txn('شحن إلى {n} محافظة', 'Shipping to {n} governorates', { n: <Num value={governoratesCount} className='font-semibold text-navy-950' /> })}</span>
                        </li>
                        <li className='flex items-center gap-2.5'>
                            <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-white text-cyan-700 shadow-soft ring-1 ring-navy-100'><Layers className='size-4.5' aria-hidden='true' /></span>
                            <span>{tx('سعر جملة وسعر قطاعي', 'Wholesale and retail prices')}</span>
                        </li>
                        <li className='flex items-center gap-2.5'>
                            <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-white text-cyan-700 shadow-soft ring-1 ring-navy-100'><Wallet className='size-4.5' aria-hidden='true' /></span>
                            {settings.paymentNote
                                ? <CmsText table='site_settings' id={settings.id} field={settings.paymentNote} />
                                : <span>{tx('تأكيد الطلب قبل الشحن', 'Order confirmed before shipping')}</span>}
                        </li>
                    </ul>
                </div>

                <div className='relative lg:col-span-6'>
                    <div className='relative mx-auto aspect-[1/1.02] w-full max-w-[560px] sm:aspect-[5/4.4]'>
                        <div className='absolute inset-x-4 inset-y-6 overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 shadow-lift sm:inset-x-10'>
                            {homepage.heroImage
                                ? <img src={homepage.heroImage} alt={homepage.heroImageAlt || homepage.heroTitle.v} className='h-full w-full object-cover' fetchPriority='high' />
                                : (
                                    <>
                                        <div className='absolute -end-20 -top-24 size-80 rounded-full bg-cyan-500/30 blur-3xl' aria-hidden='true' />
                                        <div className='absolute -bottom-28 -start-16 size-80 rounded-full bg-navy-500/40 blur-3xl' aria-hidden='true' />
                                        <div className='absolute inset-[12%]'><BrandPattern /></div>
                                    </>
                                )}
                        </div>

                        {showSpotlight && spotlight && spotlightPricing && spotlightPricing.wholesaleFinal !== null && (
                            <Link
                                href={`/products/${spotlight.slug}`}
                                className='absolute start-0 top-0 w-[82%] max-w-[360px] rounded-3xl bg-white/95 p-4 shadow-lift ring-1 ring-navy-100 backdrop-blur transition-transform hover:scale-[1.01] motion-safe:animate-float sm:p-5'
                            >
                                <div className='flex items-center gap-3'>
                                    <ProductMedia src={spotlight.image} alt={spotlight.imageAlt} categorySlug={spotlight.category?.slug} className='size-12 shrink-0 rounded-2xl ring-1 ring-navy-100' />
                                    <div className='flex min-w-0 flex-col'>
                                        <CmsText table='products' id={spotlight.id} field={spotlight.name} className='truncate text-sm font-semibold text-navy-950' />
                                        {spotlight.category && <CmsText table='categories' id={spotlight.category.id} field={spotlight.category.name} className='truncate text-xs text-muted-foreground' />}
                                    </div>
                                </div>

                                <div className='mt-4 grid grid-cols-2 gap-2'>
                                    <div className='flex flex-col gap-1 rounded-2xl bg-navy-50 p-3'>
                                        <span className='text-[11px] font-medium text-navy-500'>{tx('قطاعي', 'Retail')}</span>
                                        <Price value={spotlightPricing.retailFinal} className='text-lg font-bold text-navy-950' />
                                    </div>
                                    <div className='flex flex-col gap-1 rounded-2xl bg-cyan-50 p-3 ring-1 ring-cyan-100'>
                                        <span className='text-[11px] font-medium text-cyan-800'>
                                            {txn('جملة من {n}', 'Wholesale from {n}', { n: <Num value={spotlightPricing.wholesaleMinQty} /> })}
                                            {' '}
                                            {unitLabel(spotlight.unit, locale, spotlightPricing.wholesaleMinQty)}
                                        </span>
                                        <Price value={spotlightPricing.wholesaleFinal} className='text-lg font-bold text-navy-950' />
                                    </div>
                                </div>

                                {spotlightPricing.wholesaleSavingPct > 0 && (
                                    <p className='mt-3 text-xs font-medium text-emerald-700'>
                                        {tx('توفير', 'Save')}
                                        {' '}
                                        <span dir='ltr'>{spotlightPricing.wholesaleSavingPct}%</span>
                                        {' '}
                                        {tx('تلقائياً عند الشراء بالجملة', 'automatically when you buy wholesale')}
                                    </p>
                                )}
                            </Link>
                        )}

                        {shippingSample && (
                            <div className='absolute end-0 bottom-0 w-[64%] max-w-[300px] rounded-3xl bg-white/95 p-4 shadow-lift ring-1 ring-navy-100 backdrop-blur motion-safe:animate-float-delayed sm:p-5'>
                                <div className='flex items-center gap-3'>
                                    <span className='flex size-10 shrink-0 items-center justify-center rounded-2xl bg-navy-900 text-cyan-300'>
                                        <Truck className='size-5' aria-hidden='true' />
                                    </span>
                                    <div className='flex min-w-0 flex-col'>
                                        <span className='text-xs text-muted-foreground'>
                                            {tx('الشحن إلى', 'Shipping to')}
                                            {' '}
                                            <CmsText table='governorates' id={shippingSample.id} field={shippingSample.name} className='font-medium text-navy-900' />
                                        </span>
                                        <Price value={shippingSample.fee} className='text-base font-bold text-navy-950' />
                                    </div>
                                </div>

                                <p className='mt-3 text-xs text-navy-600'>
                                    {txn('التوصيل خلال {min} إلى {max} أيام عمل', 'Delivered in {min} to {max} business days', {
                                        min: <Num value={shippingSample.minDays} className='font-semibold text-navy-900' />,
                                        max: <Num value={shippingSample.maxDays} className='font-semibold text-navy-900' />
                                    })}
                                </p>
                            </div>
                        )}

                        {liveOffer && (
                            <Link href='/offers' className='absolute end-2 top-10 hidden max-w-[230px] items-center gap-2.5 rounded-full bg-cyan-400 py-2 ps-2 pe-4 text-navy-950 shadow-lift transition-transform hover:scale-[1.02] sm:flex'>
                                <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-navy-950 text-cyan-300'>
                                    <BadgePercent className='size-4' aria-hidden='true' />
                                </span>
                                <span className='flex min-w-0 flex-col'>
                                    <CmsText table='offers' id={liveOffer.id} field={liveOffer.title} className='truncate text-xs font-bold' />
                                    {liveOffer.endsAt && <Countdown target={liveOffer.endsAt} compact className='text-[11px] text-navy-900' />}
                                </span>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
